/**
 * Prüfungen einer E-Mail-Adresse, die ohne Netzwerk auskommen.
 * Werden auf der Website (sofortige Rückmeldung) und auf dem Server genutzt.
 * Der Server prüft zusätzlich Wegwerf-Listen, Mailserver (MX) und Häufungen.
 */

export type EmailBlockCode =
  | "invalid"
  | "disposable"
  | "reserved_domain"
  | "typo_domain"
  | "role_address"
  | "suspicious_pattern"
  | "no_mail_server"
  | "rate_limit_ip"
  | "rate_limit_domain"
  | "bot_detected";

export interface EmailBlock {
  ok: false;
  code: EmailBlockCode;
  /** Text für die Person, die sich registriert: sagt klar, warum. */
  message: string;
}

export interface EmailOk {
  ok: true;
  email: string;
  local: string;
  domain: string;
}

export type EmailCheck = EmailOk | EmailBlock;

export const SUPPORT_EMAIL = "service@handvero.de";

const CONTACT_HINT = `Bitte nutzen Sie Ihre geschäftliche Adresse oder schreiben Sie uns an ${SUPPORT_EMAIL}, dann prüfen wir Ihre Anmeldung persönlich.`;

export const BLOCK_MESSAGES: Record<EmailBlockCode, string> = {
  invalid: "Bitte geben Sie eine gültige E-Mail-Adresse ein, zum Beispiel name@firma.de.",
  disposable: `Diese E-Mail-Adresse können wir nicht annehmen, weil es sich um eine Wegwerf-Adresse handelt. ${CONTACT_HINT}`,
  reserved_domain: `Diese E-Mail-Adresse können wir nicht annehmen, weil die Domain nur für Beispiele und Tests gedacht ist. ${CONTACT_HINT}`,
  typo_domain: "Die Adresse sieht nach einem Tippfehler aus.",
  role_address: `Diese E-Mail-Adresse können wir nicht annehmen, weil an sie keine Antworten zugestellt werden (zum Beispiel „noreply“). ${CONTACT_HINT}`,
  suspicious_pattern: `Diese E-Mail-Adresse können wir nicht annehmen, weil sie automatisch erzeugt aussieht. ${CONTACT_HINT}`,
  no_mail_server: `Diese E-Mail-Adresse können wir nicht annehmen, weil für die Domain kein Mailserver eingerichtet ist. Bitte prüfen Sie die Schreibweise oder schreiben Sie uns an ${SUPPORT_EMAIL}.`,
  rate_limit_ip: `Von Ihrem Anschluss kamen in kurzer Zeit zu viele Registrierungen. Bitte versuchen Sie es in einer Stunde erneut oder schreiben Sie uns an ${SUPPORT_EMAIL}.`,
  rate_limit_domain: `Für diese Firmen-Domain wurden heute schon mehrere Konten angelegt. Weitere Mitarbeiter lädt der Inhaber im Kundenbereich ein. Bei Fragen schreiben Sie uns an ${SUPPORT_EMAIL}.`,
  bot_detected: `Diese Registrierung konnten wir nicht annehmen, weil sie automatisch ausgefüllt aussieht. Bitte versuchen Sie es noch einmal oder schreiben Sie uns an ${SUPPORT_EMAIL}.`,
};

/** Domains, die per Definition keine echten Postfächer haben (RFC 2606 / 6761) oder Testdomains sind. */
const RESERVED_DOMAINS = new Set([
  "example.com",
  "example.net",
  "example.org",
  "example.de",
  "test.de",
  "test.com",
  "beispiel.de",
  "muster.de",
  "mustermann.de",
  "domain.de",
  "domain.com",
  "email.de",
  "localhost",
]);
const RESERVED_TLDS = new Set(["test", "example", "invalid", "localhost", "local", "internal"]);

/** Häufige Tippfehler bei großen Mailanbietern → richtige Domain. */
const TYPO_DOMAINS: Record<string, string> = {
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gmail.de": "gmail.com",
  "gamil.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.con": "gmail.com",
  "gmx.dee": "gmx.de",
  "gmx.d": "gmx.de",
  "gxm.de": "gmx.de",
  "gmx.con": "gmx.net",
  "web.d": "web.de",
  "wed.de": "web.de",
  "web.dee": "web.de",
  "outlok.de": "outlook.de",
  "outllok.de": "outlook.de",
  "outlook.dee": "outlook.de",
  "outlok.com": "outlook.com",
  "hotmial.com": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "t-onlien.de": "t-online.de",
  "t-onine.de": "t-online.de",
  "tonline.de": "t-online.de",
  "freenet.dee": "freenet.de",
  "yaho.de": "yahoo.de",
  "yahoo.dee": "yahoo.de",
  "icloud.de": "icloud.com",
  "iclod.com": "icloud.com",
};

/** Adressen, an die niemand antwortet. */
const ROLE_LOCAL_PARTS = new Set([
  "noreply",
  "no-reply",
  "no_reply",
  "donotreply",
  "do-not-reply",
  "mailer-daemon",
  "postmaster",
  "abuse",
  "nobody",
  "bounce",
  "bounces",
]);

const EMAIL_RE = /^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

function block(code: EmailBlockCode, message = BLOCK_MESSAGES[code]): EmailBlock {
  return { ok: false, code, message };
}

/** Sieht der Teil vor dem @ maschinell erzeugt aus? Bewusst vorsichtig, damit echte Adressen durchkommen. */
export function looksGenerated(local: string): boolean {
  const plain = local.replace(/[._+-]/g, "");
  if (/\d{9,}/.test(plain)) return true;
  if (plain.length >= 20 && !/[aeiouäöü]/.test(plain)) return true;
  // lange, gleichmäßig gemischte Buchstaben-Ziffern-Folgen wie "x7k2p9q4m8z3w1"
  if (plain.length >= 14) {
    const digits = (plain.match(/\d/g) ?? []).length;
    const switches = (plain.match(/[a-z]\d|\d[a-z]/g) ?? []).length;
    if (digits / plain.length > 0.3 && switches >= 8) return true;
  }
  return false;
}

export function checkEmailFormat(raw: string): EmailCheck {
  const email = normalizeEmail(raw);
  if (email.length > 254 || !EMAIL_RE.test(email)) return block("invalid");
  const at = email.lastIndexOf("@");
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (local.length > 64 || local.startsWith(".") || local.endsWith(".") || local.includes("..")) {
    return block("invalid");
  }
  const tld = domain.split(".").pop() ?? "";
  if (tld.length < 2 || /^\d+$/.test(tld)) return block("invalid");

  if (RESERVED_DOMAINS.has(domain) || RESERVED_TLDS.has(tld)) return block("reserved_domain");

  const fix = TYPO_DOMAINS[domain];
  if (fix) {
    return block("typo_domain", `Die Adresse sieht nach einem Tippfehler aus. Meinten Sie ${local}@${fix}?`);
  }

  const base = local.split("+")[0] ?? local;
  if (ROLE_LOCAL_PARTS.has(base)) return block("role_address");
  if (looksGenerated(base)) return block("suspicious_pattern");

  return { ok: true, email, local, domain };
}
